<template>
  <div>
    <section aria-labelledby="catalog-title" class="catalog-hero">
      <div class="hero-photo"></div>
      <div class="technical-grid"></div>
      <div class="container hero-layout">
        <div class="reveal visible">
          <div class="breadcrumb"><a :href="RAIZ">Inicio</a><span> / </span><span>Desarrollo inmobiliario</span></div>
          <h1 id="catalog-title">Territorios que se convierten en <em>oportunidades.</em></h1>
          <p class="intro">
            Desarrollamos grandes terrenos urbanos para fines habitacionales e industriales. Desde la
            planificación del proyecto, articulamos las gestiones necesarias para evaluar su factibilidad
            técnica, legal y comercial.
          </p>
          <div class="hero-tools">
            <a class="btn blue" href="#portafolio">Explorar proyectos <span aria-hidden="true">↗</span></a>
            <a class="btn stroke" :href="`${RAIZ}#nosotros`">Conocer a Polz</a>
          </div>
        </div>
        <div aria-label="Resumen del portafolio" class="hero-plate reveal visible">
          <span>Polz Asociados / Desarrollo</span>
          <strong>08<span style="font-size: 23px; color: #8cc6ff; vertical-align: top; margin-left: 6px">↗</span></strong>
          <p>Ocho proyectos que figuran en el catálogo actual, distribuidos en diversas ciudades y regiones de Chile.</p>
          <div class="small-metrics">
            <div><b>05</b><small>Regiones</small></div>
            <div><b>Chile</b><small>Cobertura</small></div>
          </div>
        </div>
      </div>
    </section>

    <section id="portafolio" class="section">
      <div class="container">
        <div v-reveal class="section-head">
          <div>
            <div class="eyebrow">Nuestro portafolio</div>
            <h2>Proyectos con identidad y contexto territorial.</h2>
          </div>
          <p>
            Una vitrina más visual: cada terreno tiene protagonismo, características concretas y una ruta
            simple hacia su ficha de detalle.
          </p>
        </div>

        <div v-reveal class="filters">
          <div aria-label="Filtrar proyectos por región" class="category" role="group">
            <button
              v-for="r in filtros"
              :key="r.valor"
              type="button"
              class="filter"
              :aria-pressed="String(region === r.valor)"
              @click="region = r.valor"
            >
              {{ r.texto }}
            </button>
          </div>
          <label class="search-wrap">
            <input v-model="busqueda" aria-label="Buscar proyectos" placeholder="Buscar por ciudad o proyecto" type="search">
          </label>
        </div>

        <div aria-live="polite" class="results-count">
          <span>{{ visibles.length }} {{ visibles.length === 1 ? 'proyecto' : 'proyectos' }}</span> en el catálogo
        </div>

        <div class="projects-grid">
          <article
            v-for="p in visibles"
            :key="p.slug"
            v-reveal
            :aria-label="`Abrir ficha: ${p.nombre}`"
            class="project-card"
            role="link"
            tabindex="0"
            style="cursor: pointer"
            @click="abrir($event, p)"
            @keydown.enter.self.prevent="ir(p)"
            @keydown.space.self.prevent="ir(p)"
          >
            <div class="project-image">
              <img :alt="p.alt" loading="lazy" :src="foto(p.foto, 1000)" @error="conRespaldo($event, p.respaldo)">
              <span class="project-number">PROYECTO / {{ numero(p) }}</span>
              <span class="location">{{ p.ciudad }} · {{ p.regionNombre }}</span>
              <span v-if="p.sello" class="project-crest" title="Identidad gráfica del proyecto">
                <img :alt="p.nombre" :src="p.sello" @error="conRespaldo($event, p.selloRespaldo)">
              </span>
              <span v-else aria-hidden="true" class="project-crest">{{ p.monograma }}</span>
            </div>
            <div class="project-content">
              <div class="project-type">{{ p.tipo }}</div>
              <h3>{{ p.nombre }}</h3>
              <p>{{ p.descripcion }}</p>
              <div class="stats"><span v-for="s in stats(p)" :key="s">{{ s }}</span></div>
              <a :href="urlFicha(p.slug)">Ver proyecto ↗</a>
            </div>
          </article>
        </div>
        <div v-if="!visibles.length" class="empty-state" style="display: block">
          No encontramos proyectos con ese filtro. Prueba otra ciudad o región.
        </div>

        <p class="ref-note">
          Las imágenes son referenciales y deben reemplazarse por fotografías autorizadas de cada proyecto.
          Datos y nombres tomados de las fichas publicadas por Polz Asociados; el estado, disponibilidad y
          antecedentes vigentes deben verificarse antes de publicar.
        </p>

        <div v-reveal class="portfolio-end">
          <div>
            <div class="eyebrow light">Nuevo proyecto</div>
            <h3>¿Tienes un terreno con potencial?</h3>
            <p>
              El desarrollo parte con una lectura integral del suelo, su factibilidad técnica, legal y
              comercial, y los escenarios que permiten maximizar el valor de la inversión.
            </p>
          </div>
          <a class="btn blue" :href="correo('Consulta por proyecto de desarrollo')">Conversemos ↗</a>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { PROYECTOS, RAIZ, REGIONES, conRespaldo, correo, foto, urlFicha } from './datos';
import reveal from './revelar';

export default {
  name: 'PzProyectos',
  directives: { reveal },
  data() {
    return {
      RAIZ,
      filtros: [{ valor: 'todos', texto: 'Todos' }, ...REGIONES.map((r) => ({ valor: r, texto: r }))],
      region: 'todos',
      busqueda: '',
    };
  },
  computed: {
    visibles() {
      const q = this.busqueda.toLocaleLowerCase('es').trim();
      return PROYECTOS.filter((p) => (this.region === 'todos' || p.region === this.region)
        && (!q || p.busqueda.includes(q)));
    },
  },
  methods: {
    conRespaldo,
    correo,
    foto,
    urlFicha,
    numero(p) {
      return String(PROYECTOS.indexOf(p) + 1).padStart(2, '0');
    },
    stats(p) {
      return p.stats || [p.cifras[0].valor, p.ciudad];
    },
    ir(p) {
      window.location.href = urlFicha(p.slug);
    },
    // Toda la tarjeta lleva a la ficha, salvo que el clic sea en su propio enlace.
    abrir(evento, p) {
      if (evento.target.closest('a, button')) return;
      this.ir(p);
    },
  },
};
</script>
